import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  TimeRange,
  TrafficToggles,
  MetricToggles,
  ConversionToggles,
} from '@/components/analytics/types';
import type { DateRange } from '@/components/analytics/CustomRangeCalendar/CustomRangeCalendar';
import type { MetricsResponse } from './types';
import { fetchAnalytics } from './thunks';

/** Serializable version of DateRange stored in Redux (ISO strings instead of Date objects) */
export interface SerializedDateRange {
  start: string;
  end: string;
}

interface AnalyticsState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  data: MetricsResponse | null;
  error: string | null;
  timeRange: TimeRange;
  customRange: SerializedDateRange | null;
  traffic: TrafficToggles;
  metrics: MetricToggles;
  conversions: ConversionToggles;
}

const initialState: AnalyticsState = {
  status: 'idle',
  data: null,
  error: null,
  timeRange: '7 Days',
  customRange: null,
  traffic: {
    views: true,
    uniqueVisitors: true,
    conversions: true,
    publishTimestamps: false,
  },
  metrics: {
    conversionRate: true,
    bounceRate: true,
  },
  conversions: {
    buttonClicks: true,
    formSubmissions: false,
    linkClicks: false,
  },
};

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setTimeRange(state, action: PayloadAction<TimeRange>) {
      state.timeRange = action.payload;
      state.customRange = null;
      state.status = 'idle';
    },
    setCustomRange(state, action: PayloadAction<DateRange>) {
      state.customRange = {
        start: action.payload.start.toISOString(),
        end: action.payload.end.toISOString(),
      };
      state.status = 'idle';
    },
    setTrafficToggle(state, action: PayloadAction<{ key: keyof TrafficToggles; value: boolean }>) {
      state.traffic[action.payload.key] = action.payload.value;
    },
    setMetricsToggle(state, action: PayloadAction<{ key: keyof MetricToggles; value: boolean }>) {
      state.metrics[action.payload.key] = action.payload.value;
    },
    setConversionsToggle(
      state,
      action: PayloadAction<{ key: keyof ConversionToggles; value: boolean }>,
    ) {
      state.conversions[action.payload.key] = action.payload.value;
    },
    resetAnalytics(state) {
      state.status = 'idle';
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnalytics.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchAnalytics.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Failed to load analytics';
      });
  },
});

export const {
  setTimeRange,
  setCustomRange,
  setTrafficToggle,
  setMetricsToggle,
  setConversionsToggle,
  resetAnalytics,
} = analyticsSlice.actions;
export default analyticsSlice.reducer;
